import { ContactFormData } from '../types';

/**
 * פונקציה לשמירת נתוני טופס יצירת קשר ל-Google Sheets
 * 
 * הפונקציה שולחת את נתוני הטופס ל-Google Apps Script
 * שמשמר את הנתונים ב-Google Sheets
 * 
 * @param formData - נתוני הטופס (fullName, phone, email, message)
 * @throws Error - אם יש שגיאה בשליחה
 */
// 🛡️ פונקציה ליצירת hash מהנתונים (בלי timestamp) לזיהוי שליחות זהות
const createSubmissionHash = (formData: ContactFormData): string => {
  const dataString = `${formData.fullName}_${formData.phone}_${formData.email}_${formData.message}`;
  // יצירת hash פשוט (במקום MD5 כי זה frontend)
  let hash = 0;
  for (let i = 0; i < dataString.length; i++) {
    const char = dataString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

// 🛡️ פונקציה לבדיקה אם כבר בשליחה (למניעת שליחה כפולה בו-זמנית)
const isSubmittingKey = 'contact_form_is_submitting';
const submissionHashKey = 'contact_form_submission_hash';

export const saveContactToGoogleSheets = async (formData: ContactFormData): Promise<void> => {
  try {
    // 🛡️ הגנה חזקה מפני שליחה כפולה - בדיקה אם כבר בשליחה
    const isSubmitting = sessionStorage.getItem(isSubmittingKey);
    if (isSubmitting === 'true') {
      throw new Error('Form submission already in progress');
    }
    
    // 🛡️ יצירת hash מהנתונים (בלי timestamp) לזיהוי שליחות זהות
    const submissionHash = createSubmissionHash(formData);
    const lastSubmissionHash = sessionStorage.getItem(submissionHashKey);
    const lastSubmissionTime = sessionStorage.getItem('contact_form_last_submission_time');
    
    // אם יש hash זהה ושליחה לאחרונה (פחות מ-5 דקות), לא לשלוח שוב
    if (lastSubmissionHash === submissionHash && lastSubmissionTime) {
      const timeSinceLastSubmission = Date.now() - parseInt(lastSubmissionTime);
      const fiveMinutes = 5 * 60 * 1000; // 5 דקות במילישניות
      
      if (timeSinceLastSubmission < fiveMinutes) {
        throw new Error('Duplicate submission detected');
      }
    }
    
    // 🛡️ סימון שהטופס בשליחה - **לפני** השליחה!
    sessionStorage.setItem(isSubmittingKey, 'true');
    sessionStorage.setItem(submissionHashKey, submissionHash);
    sessionStorage.setItem('contact_form_last_submission_time', Date.now().toString());
    
    try {
      // שימוש ב-Environment Variables או fallback values
      // URL של Google Apps Script לטופס יצירת קשר (עם doPost - מונע Retry אוטומטי!)
      // גרסה 10 - 13 בנוב׳ 2025, 9:21
      const baseUrl = process.env.REACT_APP_GOOGLE_SHEETS_CONTACT_URL || 'https://script.google.com/macros/s/AKfycbwy0hV35cdDgLE_B3hY2JVv54HKXZU8gqNQdjw3R8XyrXAExIdzo-FXObOjECNlK2sU9w/exec';
      // Sheet ID של Google Sheet לטופס יצירת קשר
      const sheetId = process.env.REACT_APP_GOOGLE_SHEET_CONTACT_ID || '1YLLowzGL77XEdEsFfqtNNp4UaXQfWjesx4G42LhbauQ';
      
      // יצירת FormData (ל-POST)
      const timestamp = new Date().toISOString();
      const postData = new FormData();
      postData.append('type', 'contact');
      postData.append('sheetId', sheetId);
      postData.append('fullName', formData.fullName);
      postData.append('phone', formData.phone);
      postData.append('email', formData.email);
      postData.append('message', formData.message);
      postData.append('timestamp', timestamp);
      
      // Sending contact form data to Google Sheets
      
      // ✅ שליחה ל-Google Apps Script - עם POST (מונע Retry אוטומטי!)
      // ⚠️ חשוב: mode: 'no-cors' לא יכול לראות את התשובה
      // ⚠️ חשוב: עם mode: 'no-cors', צריך להשתמש ב-FormData (לא JSON)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 8000); // 8 שניות timeout
      
      try {
        // ✅ שליחה ל-Google Apps Script עם mode: 'no-cors'
        // ⚠️ חשוב: עם mode: 'no-cors', לא נוכל לראות את התשובה
        // אבל אם הבקשה נשלחה (ללא network error), הנתונים יישמרו
        await fetch(baseUrl, {
          method: 'POST',
          mode: 'no-cors',
          body: postData,
          signal: controller.signal,
          cache: 'no-store' // ✅ חשוב: לא לשמור ב-cache
        });
        clearTimeout(timeoutId);
        
        // ✅ אם לא הייתה שגיאה, הבקשה נשלחה
        // עם mode: 'no-cors', לא נוכל לראות את התשובה
        // אבל הנתונים יישמרו ב-Google Sheets והמייל יישלח
        // Request sent successfully (no-cors mode)
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        // ⚠️ עם mode: 'no-cors', שגיאות יכולות להיות:
        // 1. Network error (אין אינטרנט) - זה שגיאה אמיתית - צריך לזרוק שגיאה!
        // 2. Timeout/AbortError - יכול להיות שהבקשה בכל זאת נשלחה
        // 3. Mixed Content / CORS - הדפדפן חוסם אבל הבקשה בכל זאת נשלחה
        
        // 🛡️ בדיקה חשובה: אם אין אינטרנט, זו שגיאה אמיתית!
        const isOnline = navigator.onLine;
        
        if (fetchError instanceof Error) {
          if (fetchError.name === 'AbortError') {
            // ⚠️ Timeout - יכול להיות שהבקשה בכל זאת נשלחה
            // אבל אם אין אינטרנט, זו שגיאה אמיתית
            if (!isOnline) {
              console.error('❌ No internet connection - request failed');
              throw new Error('אין חיבור לאינטרנט. אנא בדוק את החיבור ונסה שוב.');
            }
            console.warn('⚠️ Request timeout - assuming success (no-cors mode, internet available)');
            // לא נזרוק שגיאה - נמשיך הלאה
          } else if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('Mixed Content')) {
            // ⚠️ Mixed Content / CORS - הדפדפן חוסם אבל הבקשה בכל זאת נשלחה
            // אבל אם אין אינטרנט, זו שגיאה אמיתית!
            if (!isOnline) {
              console.error('❌ No internet connection - request failed');
              throw new Error('אין חיבור לאינטרנט. אנא בדוק את החיבור ונסה שוב.');
            }
            // יש אינטרנט אבל יש Mixed Content - הבקשה כנראה נשלחה
            console.warn('⚠️ Mixed Content / CORS error - assuming success (internet available)');
            // לא נזרוק שגיאה - נמשיך הלאה
          } else {
            // שגיאה אחרת - נזרוק שגיאה (זה לא Mixed Content)
            console.error('❌ Network error:', fetchError);
            throw fetchError;
          }
        } else {
          // שגיאה לא מוכרת - נבדוק אם יש אינטרנט
          if (!isOnline) {
            console.error('❌ No internet connection - request failed');
            throw new Error('אין חיבור לאינטרנט. אנא בדוק את החיבור ונסה שוב.');
          }
          // יש אינטרנט אבל יש שגיאה לא מוכרת - נניח שהבקשה הצליחה
          console.warn('⚠️ Unknown error - assuming success (internet available):', fetchError);
          // לא נזרוק שגיאה - נמשיך הלאה
        }
      }
      
      // Contact form data sent successfully
      
      // מנקים את localStorage הישן (אם יש) - **רק אחרי הצלחה!**
      localStorage.removeItem('pendingContactData');
      localStorage.removeItem('last_contact_submission');
      
    } finally {
      // 🛡️ הסרת הסימון שהטופס בשליחה (גם אם יש שגיאה)
      // אבל נשאיר את ה-hash ואת הזמן כדי למנוע שליחה כפולה
      sessionStorage.removeItem(isSubmittingKey);
    }
    
    // אם הצלחנו, אין צורך לעשות כלום נוסף
    // הטיפול בהצלחה/כשלון נעשה ב-ContactForm.tsx
    
  } catch (error) {
    // ⚠️ חשוב: לא לשמור ב-localStorage כגיבוי!
    // זה יכול לגרום לשליחה חוזרת אוטומטית אחרי זמן מה
    // במקום זאת, רק לזרוק שגיאה ולתת למשתמש לנסות שוב
    
    // זורק את השגיאה כדי שהטופס יוכל לטפל בה
    console.error('❌ Error saving contact form to Google Sheets:', error);
    
    // מנקים את sessionStorage רק אם זו שגיאת duplicate או submission in progress
    // (כי אז אנחנו לא רוצים לחסום ניסיון עתידי)
    if (error instanceof Error) {
      if (error.message === 'Duplicate submission detected' || error.message === 'Form submission already in progress') {
        // מנקים את הסימון כדי לאפשר ניסיון עתידי
        sessionStorage.removeItem(isSubmittingKey);
      }
    }
    
    throw error;
  }
};

